import { job as jobQuery, jobs as jobsQuery } from '@data/queries';
import { Container, Grid, Stack } from '@mui/material';
// @mui
import { styled } from '@mui/material/styles';
import sanity from '@utils/sanity';
import { ReactElement } from 'react';
// @types
import { JobProps } from 'src/@types/job';
// components
import { LoadingScreen, Page } from 'src/components';
// config
import { HEADER_DESKTOP_HEIGHT, HEADER_MOBILE_HEIGHT } from 'src/config';
// hooks
import { useResponsive } from 'src/hooks';
// layouts
import Layout from 'src/layouts';
import JobCompanyInfo from 'src/sections/jobs/JobCompanyInfo';
import JobDetails from 'src/sections/jobs/JobDetails';
import JobHero from 'src/sections/jobs/JobHero';
import JobInfo from 'src/sections/jobs/JobInfo';


// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  paddingTop: HEADER_MOBILE_HEIGHT,
  [theme.breakpoints.up('md')]: {
    paddingTop: HEADER_DESKTOP_HEIGHT,
  },
}));

// ----------------------------------------------------------------------

type Props = {
  job: JobProps;
  jobs: JobProps[];
};

const JobPage = ({ job }: Props) => {
  const isDesktop = useResponsive('up', 'md');

  if (!job) {
    return <LoadingScreen />;
  }

  return (
    <Page seo={job.seo}>
      <RootStyle>
        <JobHero job={job} />

        <Container
          sx={{
            pt: { xs: 10, md: 8 },
            pb: { xs: 15, md: 8 },
          }}
        >
          <Grid container spacing={8}>
            {!isDesktop && (
              <Grid item xs={12} md={5} lg={4}>
                <JobInfo job={job} />
              </Grid>
            )}

            <Grid item xs={12} md={7} lg={8}>
              <JobDetails job={job} />
            </Grid>

            <Grid item xs={12} md={5} lg={4}>
              <Stack spacing={5}>
                {isDesktop && <JobInfo job={job} />}
                <JobCompanyInfo />
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </RootStyle>
    </Page>
  );
}

// ----------------------------------------------------------------------

JobPage.getLayout = function getLayout(page: ReactElement) {
  return <Layout>{page}</Layout>;
};


export const getStaticPaths = async () => {
  const jobs = await sanity.fetch(jobsQuery);
  const paths = jobs.map((job: JobProps) => ({
    params: { slug: job.slug.current },
  }));

  return { paths, fallback: false };
};

export const getStaticProps = async ({ params }: any) => {
  const job = await sanity.fetch(jobQuery, { slug: params.slug });
  const jobs = await sanity.fetch(jobsQuery);
  return { props: { job, jobs } };
};

export default JobPage;